import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Filter,
  X,
  Eye,
  Mail,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Tag,
  Target,
  Zap,
  AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { QueryPreviewSkeleton } from './QueryPreviewSkeleton';

// Helper functions
const getQueryId = (query) => {
  return query.support_query_id || query.id || query.query_id || query._id;
};

const getQueryText = (query) => {
  return query.query_text || query.query || query.question || query.message || 'No query text available';
};

const getFormattedSource = (query) => {
  if (query.source_url) {
    try {
      const url = new URL(query.source_url);
      const hostname = url.hostname.replace('www.', '');
      return `${hostname}${url.pathname}`;
    } catch (e) {
      return query.source_url;
    }
  }
  return query.source || 'Unknown';
};

const getEmotionEmoji = (emotion) => {
  const emotionEmojis = {
      // Positive emotions
      'happy': '😊', 'excited': '🎉', 'joy': '😄', 'love': '❤️', 'satisfied': '😌',
      'grateful': '🙏', 'appreciation': '🙏', 'proud': '😎', 'relief': '😮‍💨',
      'calm': '😌', 'content': '☺️', 'enthusiastic': '🤩', 'optimistic': '😃',
      'hopeful': '🌟', 'amused': '😄', 'delighted': '😁',
      // Negative emotions
      'angry': '😠', 'frustrated': '😤', 'sad': '😢', 'disappointed': '😞',
      'upset': '😔', 'annoyed': '😒', 'irritated': '😑', 'furious': '🤬',
      'hurt': '💔', 'depressed': '😭', 'miserable': '😣', 'bitter': '😖',
      'resentful': '😾',
      // Anxiety/Fear related
      'anxious': '😰', 'anxiety': '😰', 'worried': '😟', 'nervous': '😬',
      'stressed': '😓', 'overwhelmed': '😵', 'fearful': '😨', 'scared': '😱',
      'panicked': '😰', 'tense': '😬',
      // Confusion/Uncertainty
      'confused': '😕', 'uncertain': '🤔', 'doubtful': '🤨', 'skeptical': '🤨',
      'puzzled': '😵‍💫', 'perplexed': '😵', 'bewildered': '😳',
      // Neutral
      'neutral': '😐', 'indifferent': '😶', 'bored': '😑', 'tired': '😴',
      // Surprise
      'surprised': '😲', 'shocked': '😱', 'amazed': '😮', 'astonished': '🤯',
      // Urgency/Action
      'urgent': '⚡', 'impatient': '⏰', 'rushed': '🏃',
      // Other
      'curious': '🤔', 'interested': '👀', 'disgusted': '🤢', 'embarrassed': '😳',
      'shame': '😳', 'guilty': '😔', 'lonely': '😔', 'jealous': '😒', 'envious': '😤'
  };
  const normalizedEmotion = emotion?.toLowerCase().trim();
  return emotionEmojis[normalizedEmotion] || '💭';
};

const QueryPreviewModal = ({ isOpen, onClose, queries, title, filterType, filterValue, loading, pagination, onPageChange }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleViewAll = () => {
    // Build filter object matching FilterSidebar structure
    let filterParams = {};
    
    if (filterType === 'root_cause_category') {
      filterParams = {
        root_cause: {
          category: [filterValue]
        }
      };
    } else if (filterType === 'emotion') {
      filterParams = {
        sentiment: {
          emotion: [filterValue]
        }
      };
    } else if (filterType === 'primary_category') {
      filterParams = {
        classification: {
          primary_category: [filterValue]
        }
      };
    } else if (filterType === 'intent') {
      filterParams = {
        classification: {
          intent: [filterValue]
        }
      };
    }

    // Encode as JSON string for URL
    const filterString = encodeURIComponent(JSON.stringify(filterParams));
    window.open(`/support?filters=${filterString}`, '_blank');
    onClose();
  };

  const handleQueryClick = (query) => {
    const queryId = getQueryId(query);
    const ticketId = query.ticket_id || queryId;
    
    if (ticketId) {
      // Navigate to support page with ticket ID in URL
      window.open(`/support/${ticketId}`, '_blank');
    } else {
      console.error('Ticket/Query ID not found:', query);
      toast.error('Unable to open query details');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex items-center justify-center min-h-screen text-center sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true"></div>

        <div 
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:align-middle sm:max-w-4xl sm:w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-black px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Filter className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{title}</h3>
                  <p className="text-sm text-gray-300 mt-0.5">
                    {queries?.length || 0} queries found
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-6 py-4 max-h-[80vh] overflow-y-auto">
            {loading ? (
              <QueryPreviewSkeleton />
            ) : queries && queries.length > 0 ? (
              <div className="space-y-3">
                {queries.slice(0, 10).map((query, index) => {
                  const queryId = getQueryId(query);
                  const ticketId = query.ticket_id || queryId;
                  const queryText = getQueryText(query);
                  
                  // Extract analysis data
                  const sentiment = query.sentiment?.emotion || query.nlp_data?.sentiment?.emotion;
                  const intent = query.classification?.intent || query.nlp_data?.query_classification?.intent;
                  const category = query.classification?.primary_category || query.nlp_data?.query_classification?.primary_category;
                  const rootCause = query.root_cause?.category || query.nlp_data?.root_cause?.category;

                  return (
                    <div
                      key={queryId || index}
                      className="border border-gray-200 rounded-lg p-4 hover:border-black hover:shadow-md transition-all cursor-pointer"
                      onClick={() => handleQueryClick(query)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center flex-wrap gap-2 mb-2 text-xs">
                            {query.created_at && (
                              <span className="text-gray-500 font-medium">{new Date(query.created_at).toLocaleDateString()}</span>
                            )}
                            <span className="font-semibold text-black bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                              #{ticketId || 'N/A'}
                            </span>
                            <span className={`font-medium px-2 py-0.5 rounded ${
                              query.status === 'responded' ? 'bg-gray-100 text-black border border-gray-200' :
                              query.status === 'pending' ? 'bg-gray-100 text-black border border-gray-200' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {query.status || 'unknown'}
                            </span>
                            <span className="text-gray-500" title={query.source_url || query.source}>
                              via {getFormattedSource(query)}
                            </span>
                          </div>

                          <p className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">
                            {queryText}
                          </p>
                          
                          {/* Minimal Analysis Tags */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {sentiment && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200" title={`Sentiment: ${sentiment}`}>
                                <span className="mr-1">{getEmotionEmoji(sentiment)}</span>
                                {sentiment}
                              </span>
                            )}
                            {intent && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-black border border-gray-200" title={`Intent: ${intent}`}>
                                <Target className="h-3 w-3 mr-1" />
                                {intent.replace(/_/g, ' ')}
                              </span>
                            )}
                            {category && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-black border border-gray-200" title={`Category: ${category}`}>
                                <Tag className="h-3 w-3 mr-1" />
                                {category.replace(/_/g, ' ')}
                              </span>
                            )}
                            {rootCause && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-black border border-gray-200" title={`Root Cause: ${rootCause}`}>
                                <Zap className="h-3 w-3 mr-1" />
                                {rootCause.replace(/_/g, ' ')}
                              </span>
                            )}
                          </div>

                          {/* Customer Info */}
                          {(query.name || query.customer_email || query.mobile) && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 border-t border-gray-100 text-xs text-gray-600">
                              {query.name && (
                                <div className="flex items-center gap-1">
                                  <span className="font-medium text-gray-700">Name:</span> {query.name}
                                </div>
                              )}
                              {query.customer_email && (
                                <div className="flex items-center gap-1">
                                  <span className="font-medium text-gray-700">Email:</span> {query.customer_email}
                                </div>
                              )}
                              {query.mobile && (
                                <div className="flex items-center gap-1">
                                  <span className="font-medium text-gray-700">Mobile:</span> {query.mobile}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <Eye className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Mail className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No queries found for this filter</p>
              </div>
            )}
          </div>

          {/* Footer with Pagination */}
          <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors order-2 sm:order-1"
            >
              Close
            </button>

            {pagination && pagination.total_pages > 1 && (
              <div className="flex items-center space-x-1 order-1 sm:order-2">
                <button
                  onClick={() => onPageChange(pagination.current_page - 1)}
                  disabled={!pagination.has_previous}
                  className="p-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 rounded-lg transition-colors"
                  title="Previous Page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <div className="hidden sm:flex items-center space-x-1">
                  {[...Array(pagination.total_pages)].map((_, index) => {
                    const page = index + 1;
                    const isActive = page === pagination.current_page;

                    // Show first, last, current, and adjacent pages
                    if (
                      page === 1 ||
                      page === pagination.total_pages ||
                      (page >= pagination.current_page - 1 && page <= pagination.current_page + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => onPageChange(page)}
                          className={`w-8 h-8 flex items-center justify-center text-xs font-medium rounded-lg transition-all duration-200 ${
                            isActive
                              ? 'bg-black text-white shadow-sm'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      (page === pagination.current_page - 2 && pagination.current_page > 3) ||
                      (page === pagination.current_page + 2 && pagination.current_page < pagination.total_pages - 2)
                    ) {
                      return (
                        <span key={page} className="px-1 text-xs text-gray-400">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>
                
                {/* Mobile Page Indicator */}
                <span className="text-xs text-gray-600 sm:hidden px-2">
                  Page {pagination.current_page} of {pagination.total_pages}
                </span>

                <button
                  onClick={() => onPageChange(pagination.current_page + 1)}
                  disabled={!pagination.has_next}
                  className="p-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 rounded-lg transition-colors"
                  title="Next Page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
            
            {queries && queries.length > 0 && (
              <button
                onClick={handleViewAll}
                className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 order-3"
              >
                <span className="hidden sm:inline">View All</span>
                <span className="sm:hidden">All</span>
                <ExternalLink className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryPreviewModal;
